import type { ReactNode } from 'react';

/**
 * Informace o aplikaci — přehled technologií, hostingu, databází, integrací
 * a modulů. Statická „dokumentace pro lidi" dostupná všem přihlášeným.
 * Když se něco zásadního změní (nová DB, doména, integrace), aktualizuj tady.
 */
export function InfoPage() {
  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text">Informace o aplikaci</h1>
        <p className="text-sm text-text-secondary mt-0.5 max-w-2xl">
          Přehled toho podstatného — technologie, kde to běží, jaké databáze používáme, integrace a
          moduly. Stav k 25. 7. 2026.
        </p>
      </div>

      {/* Zvýrazněný blok o modulu Výroba — nejdůležitější a nejsložitější část. */}
      <section className="mb-6 bg-surface rounded-xl shadow-card p-5 border-l-4 border-primary">
        <h2 className="text-base font-bold text-text mb-1">
          Modul Výroba — jak to funguje (důležité)
        </h2>
        <p className="text-sm text-text-secondary mb-3">
          Výroba má dva kroky a sahá do <strong className="text-text">dvou různých databází</strong>
          :
        </p>
        <div className="space-y-3">
          <div>
            <div className="text-sm font-semibold text-text">
              1) Zadání do výroby → naše vlastní databáze (Cloudflare D1)
            </div>
            <p className="text-sm text-text-secondary">
              Termíny, výrobní zakázky a zadání kusů do výroby se evidují v <strong>naší D1</strong>{' '}
              (tabulky <Mono>production_terms</Mono>, <Mono>production_orders</Mono>,{' '}
              <Mono>vyroba_stock_movements</Mono>). Tady jsme zdroj pravdy my.
            </p>
          </div>
          <div>
            <div className="text-sm font-semibold text-text">
              2) Naskladnění z výroby → databáze SKLADU V-Martu na kzo11 (NE Brani!)
            </div>
            <p className="text-sm text-text-secondary">
              Když naskladňujeme z výroby na skladovou pozici, zapisujeme{' '}
              <strong className="text-text">přímo do skladového ERP V-Martu</strong> — MySQL{' '}
              <Mono>d364315_erp</Mono> na <Mono>kzo11.vas-server.cz</Mono> (tabulky{' '}
              <Mono>products</Mono>, <Mono>product_stocks</Mono>, <Mono>stock_movements</Mono>).{' '}
              <strong className="text-danger">Pozor: tohle není Brani sklad</strong> — je to vlastní
              skladový systém V-Martu.
            </p>
          </div>
          <div>
            <div className="text-sm font-semibold text-text">Jak se do kzo11 vůbec dostáváme</div>
            <p className="text-sm text-text-secondary">
              Cloudflare neumí přímo do MySQL, proto to obsluhuje PHP most{' '}
              <Mono>vibecoding-vyroba-sklad.php</Mono> — kód je v repu ve složce{' '}
              <Mono>external-scripts/</Mono>, nasazený je na FTP kzo11 a běží na{' '}
              <Mono>https://v-martsystem.cz/vibecoding-vyroba-sklad.php</Mono> (přes secret{' '}
              <Mono>SKLAD_API_URL</Mono>). ⚠️ Tenhle soubor nemazat.
            </p>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <Card title="Přehled">
          <p className="text-sm text-text-secondary leading-relaxed">
            Interní sada nástrojů pro V-Mart (<Mono>tools.v-martsystem.cz</Mono>). Jeden projekt,
            více modulů — od denního porovnání skladových stavů (Sklad-Sync) přes propojení produktů
            až po výrobu (zadání do výroby, naskladnění z výroby). Přístup jen po přihlášení,
            jednotlivé moduly se zapínají per uživatel.
          </p>
        </Card>

        <Card title="Kde to běží">
          <KV k="Zdrojový kód (tento terminál)">
            <Mono>/Users/vojtechmartinek/Desktop/Claude/Tools</Mono>
          </KV>
          <KV k="Git / GitHub">
            <Mono>github.com/vojtechmartinek/sklad-sync</Mono> · větev <Mono>main</Mono>
          </KV>
          <KV k="Hosting">Cloudflare Pages (projekt „sklad-sync") + Pages Functions</KV>
          <KV k="Domény">
            <Mono>tools.v-martsystem.cz</Mono> (Tools), <Mono>vyroba.v-martsystem.cz</Mono> (modul
            Výroba, stejný projekt), dev na <Mono>*.pages.dev</Mono>
          </KV>
          <KV k="DNS">vas-hosting (kořen a subdomény míří na kzo11; jen tools. → Cloudflare)</KV>
        </Card>

        <Card title="Technologie">
          <ul className="text-sm text-text-secondary space-y-1">
            {TECH.map((t) => (
              <li key={t.name} className="flex justify-between gap-3">
                <span className="text-text">{t.name}</span>
                <span className="font-mono text-text-muted text-xs">{t.ver}</span>
              </li>
            ))}
          </ul>
        </Card>

        <Card title="Databáze">
          <KV k="Cloudflare D1 (hlavní)">
            <Mono>sklad-sync-db-dev</Mono> — SQLite, binding <Mono>DB</Mono>. Drží všechny moduly
            Tools (produkty, snapshoty, výroba, propojení, …).
          </KV>
          <KV k="R2 (úložiště)">
            <Mono>UPSELL_IMAGES</Mono> — obrázky bannerů modulu Upsell.
          </KV>
          <div className="mt-2 pt-2 border-t border-border-light">
            <div className="text-xs font-semibold text-text-muted mb-1">
              Externí MySQL na kzo11.vas-server.cz (88.86.124.16)
            </div>
            <KV k="d364315_users">
              tabulka <Mono>vyrobniSystemEvidence</Mono> — starý výrobní systém, sklad-sync z něj od
              11. 8. 2026 už nečte (plán výroby bere z naší D1).
            </KV>
            <KV k="d364315_erp">
              skladový ERP (Laravel): <Mono>products</Mono>, <Mono>stock_locations</Mono>,{' '}
              <Mono>product_stocks</Mono>, <Mono>stock_movements</Mono> (čtení i zápis).
            </KV>
          </div>
        </Card>

        <Card title="Externí PHP wrappery">
          <p className="text-sm text-text-secondary mb-2">
            Cloudflare neumí přímo do MySQL, proto malé PHP mosty na hostingu kzo11. Kód je v{' '}
            <Mono>external-scripts/</Mono> (v repu placeholdery, na FTP vyplněné). ⚠️ Nemazat.
          </p>
          <KV k="vyrobni-system.php">
            <Mono>www.v-martsystem.cz/script/vratky/…</Mono> — od 11. 8. 2026 už sklad-sync nečte,
            plán výroby bere přímo z naší D1 (<Mono>vyroba_stock_movements</Mono>).
          </KV>
          <KV k="vibecoding-vyroba-sklad.php">
            <Mono>v-martsystem.cz/vibecoding-vyroba-sklad.php</Mono> — čte pozice/stav a zapisuje
            naskladnění do ERP (secret <Mono>SKLAD_API_URL</Mono>).
          </KV>
        </Card>

        <Card title="Integrace">
          <ul className="text-sm text-text-secondary space-y-1 list-disc pl-4">
            <li>
              <span className="text-text">Shoptet</span> — katalog produktů z{' '}
              <Mono>productsMarketing.xml</Mono> (sync 1×/h), zápis dostupnosti/stavu.
            </li>
            <li>
              <span className="text-text">Brani</span> — dobíhá jen jako dual-write při naskladnění
              (chystáme se ho opustit), Sklad-Sync z něj od 11. 8. 2026 už nečte.
            </li>
            <li>
              <span className="text-text">Výrobní systém</span> — plán výroby přímo z naší D1.
            </li>
            <li>
              <span className="text-text">Skladový ERP (kzo11)</span> — naskladnění na pozice +
              zdroj fyzického skladu pro Sklad-Sync (<Mono>?action=stock_all</Mono>).
            </li>
            <li>
              <span className="text-text">Skladové nároky</span> — Shoptet{' '}
              <Mono>perStockAmounts.claim</Mono>, CZ + SK dohromady (Sklad-Sync).
            </li>
            <li>
              <span className="text-text">Resend</span> — e-mailové reporty.
            </li>
            <li>
              <span className="text-text">Telegram</span> — alerty (feed/snapshot).
            </li>
            <li>
              <span className="text-text">GA4</span> — analytics (Data API).
            </li>
          </ul>
        </Card>

        <Card title="Moduly">
          <div className="flex flex-wrap gap-1.5">
            {MODULES.map((m) => (
              <span
                key={m}
                className="text-xs bg-bg border border-border rounded-full px-2.5 py-1 text-text-secondary"
              >
                {m}
              </span>
            ))}
          </div>
        </Card>

        <Card title="Cron (automatika)">
          <p className="text-sm text-text-secondary">
            Cloudflare Workers Cron (<Mono>apps/cron-worker</Mono>) volá HTTP endpointy: sync feedu
            produktů, sync objednávek, sync skladů (upsell), produkt týdne a denní snapshot
            Sklad-Syncu. Chráněno sdíleným <Mono>CRON_SECRET</Mono>.
          </p>
        </Card>

        <Card title="Bezpečnost & secrety">
          <p className="text-sm text-text-secondary mb-2">
            Přihlášení e-mailem + heslo (PBKDF2, 100k iterací). Session 8 h, cookie{' '}
            <Mono>HttpOnly</Mono>/<Mono>Secure</Mono>/<Mono>SameSite=Lax</Mono>. SQL vždy přes{' '}
            <Mono>.bind()</Mono>, validace Zodem.
          </p>
          <div className="text-xs font-semibold text-text-muted mb-1">
            Secrets v Cloudflare (jen názvy, hodnoty nikde v kódu):
          </div>
          <div className="flex flex-wrap gap-1">
            {SECRETS.map((s) => (
              <span
                key={s}
                className="text-[11px] font-mono bg-bg border border-border rounded px-1.5 py-0.5 text-text-muted"
              >
                {s}
              </span>
            ))}
          </div>
        </Card>

        <Card title="Deploy & příkazy">
          <ul className="text-sm text-text-secondary space-y-1 font-mono text-xs">
            <li>npm run dev — lokální vývoj</li>
            <li>npm run typecheck / lint / test / build</li>
            <li>npm run deploy:dev — nasazení na dev</li>
            <li>deploy:prod — jen s výslovným souhlasem</li>
            <li>wrangler d1 migrations apply … — DB migrace</li>
          </ul>
          <p className="text-xs text-text-muted mt-2">
            Podrobnosti v <Mono>CLAUDE.md</Mono> (root) a <Mono>external-scripts/README.md</Mono>.
          </p>
        </Card>
      </div>
    </>
  );
}

const TECH: { name: string; ver: string }[] = [
  { name: 'React', ver: '18.3' },
  { name: 'Vite', ver: '5.4' },
  { name: 'TypeScript', ver: '5.6' },
  { name: 'Tailwind CSS', ver: 'v4 (beta)' },
  { name: 'Hono (backend)', ver: '4.6' },
  { name: 'Zod', ver: '3.23' },
  { name: 'React Router', ver: '6.28' },
  { name: 'Cloudflare Pages + D1 + R2', ver: '—' },
  { name: 'Biome (lint/format)', ver: '1.9' },
  { name: 'Vitest', ver: '2.1' },
  { name: 'wrangler', ver: '3.91' },
];

const MODULES = [
  'Sklad-Sync',
  'Propojení produktů',
  'Výroba: Termíny',
  'Výroba: Výrobní zakázky',
  'Výroba: Zadání do výroby',
  'Výroba: Naskladnění z výroby',
  'Napojení e-shopů',
  'Upsell & Cross-sell',
  'Odpočty',
  'Produkt týdne',
  'Hlídací pes',
  'Analytics (GA4)',
  'Historie prodeje',
  'Databáze produktů/objednávek',
  'Správa uživatelů',
];

const SECRETS = [
  'SHOPTET_API_TOKEN_CZ/SK',
  'BRANI_*',
  'SKLAD_API_URL/KEY',
  'RESEND_API_KEY',
  'SESSION_SECRET',
  'TELEGRAM_*',
  'GA4_*',
  'CRON_SECRET',
];

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="bg-surface rounded-xl shadow-card p-5">
      <h2 className="text-sm font-bold text-text mb-3">{title}</h2>
      {children}
    </section>
  );
}

function KV({ k, children }: { k: string; children: ReactNode }) {
  return (
    <div className="mb-2 last:mb-0">
      <div className="text-xs font-semibold text-text-muted">{k}</div>
      <div className="text-sm text-text-secondary">{children}</div>
    </div>
  );
}

function Mono({ children }: { children: ReactNode }) {
  return <span className="font-mono text-xs text-text bg-bg px-1 py-0.5 rounded">{children}</span>;
}
